import { useCallback, useEffect, useState } from 'react';
import type { Entrega, FichaEntrega } from '../../api/ops/entregas';
import type { ListaEntregas } from '../../api/ops/clientes';
import type { ResumenCliente } from '../../api/ops/pagos';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Tabla } from '../../componentes/Tabla';
import { Ventana } from '../../componentes/Ventana';
import { rolesDe } from '../../logica/acceso';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { diaYHora } from '../../logica/fechasCortas';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { CANALES, FILTROS_ENVIADOS, LLEGO, MOTIVO, PERMISOS_MENSAJES, RESULTADO, consultaEnviados, cuerpoReenvio, idsDeClientes, nombreIdioma, type CanalReenvio, type FiltroEnviados } from './reglas';
import { AYUDA_ENVIADOS } from './textos';

/**
 * «Enviados» (5.5): el registro de entregas del negocio (`listDeliveries`), con filtros sin cifras (Todos · No han llegado
 * · Salieron por email, `used_fallback` G304 · No se enviaron; G140), «Ver las entregas» (tipo y desde cuándo) y «Buscar
 * un cliente». Cada fila: el mensaje, «de qué es» (`deliverable_label`, G302), el cliente (sus nombres con una sola
 * llamada por página, `listCustomers` con `ids`, G303), por dónde salió con el destino enmascarado, «¿Llegó?» y cuándo.
 * «Anterior / Siguiente» sin total (G121). Sin cifra en el menú (G305). La ficha (`getDelivery`) enseña cada intento y
 * «Reenviar» con ventana de confirmación (avisa al cliente: sin «Deshacer», G159), por «El que prefiere» o por «Email».
 */
const TIPOS: readonly { valor: string; texto: string }[] = [
    { valor: '', texto: 'Todos los tipos' },
    { valor: 'service', texto: 'De servicio' },
    { valor: 'marketing', texto: 'Comerciales' },
];
const CUANDO: readonly { valor: string; texto: string }[] = [
    { valor: '30', texto: 'Últimos 30 días' },
    { valor: '7', texto: 'Últimos 7 días' },
    { valor: '90', texto: 'Últimos 90 días' },
    { valor: '', texto: 'Desde el principio' },
];

const PIDIO: Readonly<Record<string, string>> = { customer: 'El cliente', member: 'Alguien del equipo', system: 'Reservaria' };

export function Enviados(props: PropsPantalla) {
    const { ops, negocio, movil, membresia, nombreNegocio, navegar, soloLectura, busqueda } = props;
    const permisos = usePermisos();
    const puede = permisos.puede(PERMISOS_MENSAJES.registro);
    const verClientes = permisos.puede(PERMISOS_MENSAJES.clientes);
    const verPlantillas = permisos.puede(PERMISOS_MENSAJES.plantillas);
    const [filtro, setFiltro] = useState<FiltroEnviados>(0);
    const [tipo, setTipo] = useState('');
    const [dias, setDias] = useState('30');
    const [cliente, setCliente] = useState<{ id: string; nombre: string } | null>(null);
    const [buscar, setBuscar] = useState(false);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const [cargada, setCargada] = useState<{ vista: string; lista: ListaEntregas | null; error: unknown } | null>(null);
    const [nombres, setNombres] = useState<Readonly<Record<string, string>>>({});
    const [titulos, setTitulos] = useState<Readonly<Record<string, string>>>({});
    const [intento, setIntento] = useState(0);
    const [abierta, setAbierta] = useState<string | null>(busqueda.get('entrega'));

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    const consulta = consultaEnviados(filtro, { tipo: tipo === '' ? null : (tipo as 'service' | 'marketing'), desdeDias: dias === '' ? null : Number(dias), cliente: cliente?.id ?? null });
    // La fecha de «desde» cambia cada milisegundo: la vista se fija con los filtros, no con la hora.
    const vista = `${String(filtro)}|${tipo}|${dias}|${cliente?.id ?? ''}|${pag.cursor ?? ''}|${String(intento)}`;
    useEffect(() => {
        if (!puede) {
            return;
        }
        let vivo = true;
        ops.listDeliveries(negocio, { ...consulta, cursor: pag.cursor ?? undefined })
            .then((l) => {
                if (!vivo) {
                    return;
                }
                setCargada({ vista, lista: l, error: null });
                // Los nombres de los clientes de la página, en una sola llamada (G303).
                const ids = idsDeClientes(l.data);
                if (verClientes && ids.length > 0) {
                    ops.listCustomersByIds(negocio, ids)
                        .then((r) => {
                            if (vivo) {
                                setNombres((n) => ({ ...n, ...Object.fromEntries(r.data.map((c: ResumenCliente) => [c.id, c.display_name])) }));
                            }
                        })
                        .catch(() => undefined);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCargada({ vista, lista: null, error: e });
                }
            });
        return () => {
            vivo = false;
        };
        // `consulta` cambia con la hora; la vista la resume.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, puede, verClientes, vista]);

    // El nombre de cada tipo de mensaje («Confirmación de reserva») sale de las plantillas, si la persona las ve.
    useEffect(() => {
        if (!puede || !verPlantillas) {
            return;
        }
        let vivo = true;
        ops.listDeliveryTemplates(negocio, { limit: 100 })
            .then((l) => {
                if (vivo) {
                    setTitulos(Object.fromEntries(l.data.filter((p) => p.title !== undefined).map((p) => [p.message_type, p.title ?? ''])));
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, puede, verPlantillas]);

    useCabecera({
        ayuda: puede ? AYUDA_ENVIADOS : [],
        acciones: puede && verClientes ? [{ clave: 'buscar', texto: 'Buscar un cliente', icono: 'search', secundaria: true, onClick: () => { setBuscar((b) => !b); } }] : [],
    });

    if (!puede) {
        return <EstadoSinPermiso seccion="Enviados" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    const actual = cargada?.vista === vista ? cargada : null;
    if (actual?.error != null && actual.lista === null) {
        return <EstadoError seccion="los mensajes enviados" codigoSoporte={estadoDeError(actual.error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    const lista = actual?.lista ?? null;
    const tituloDe = (e: Pick<Entrega, 'message_type'>) => titulos[e.message_type] ?? null;

    return (
        <div className="rv-ca-apartado">
            <Filtros etiqueta="Qué entregas ver" items={FILTROS_ENVIADOS.map((etiqueta) => ({ etiqueta }))} valor={filtro} onCambio={(i) => { setFiltro(i as FiltroEnviados); reiniciar(); }} />
            <div className="rv-en-filtros">
                <div className="rv-en-filtros__gravedad"><Desplegable etiqueta="Tipo" valor={tipo} opciones={TIPOS} onChange={(v) => { setTipo(v); reiniciar(); }} /></div>
                <div className="rv-en-filtros__gravedad"><Desplegable etiqueta="Cuándo" valor={dias} opciones={CUANDO} onChange={(v) => { setDias(v); reiniciar(); }} /></div>
            </div>
            {cliente !== null && (
                <div className="rv-en-chip-fila">
                    <Chip estado="neutral">{`Cliente: ${cliente.nombre}`}</Chip>
                    <Boton tipo="fantasma" icono="x" movil={movil} onClick={() => { setCliente(null); reiniciar(); }}>Quitar</Boton>
                </div>
            )}
            {buscar && (
                <BuscarCliente
                    {...props}
                    onElegir={(c) => {
                        setCliente(c);
                        setBuscar(false);
                        reiniciar();
                    }}
                />
            )}
            {lista === null ? (
                <EstadoCargando seccion="los mensajes enviados" />
            ) : (
                <Tabla
                    etiqueta="Mensajes enviados"
                    movil={movil}
                    columnas={[
                        { clave: 'mensaje', titulo: 'Mensaje' },
                        { clave: 'cliente', titulo: 'Cliente' },
                        { clave: 'canal', titulo: 'Por dónde' },
                        { clave: 'llego', titulo: '¿Llegó?' },
                        { clave: 'cuando', titulo: 'Cuándo' },
                    ]}
                    filas={lista.data.map((e) => {
                        const [texto, chip] = LLEGO[e.status];
                        const titulo = tituloDe(e);
                        return {
                            clave: e.id,
                            etiqueta: titulo ?? e.deliverable_label ?? 'Mensaje',
                            onAbrir: () => { setAbierta(e.id); },
                            celdas: {
                                mensaje: (
                                    <span className="rv-en-celda">
                                        <strong>{titulo ?? e.deliverable_label ?? 'Mensaje'}</strong>
                                        {titulo !== null && e.deliverable_label != null && <small>{e.deliverable_label}</small>}
                                    </span>
                                ),
                                cliente: e.customer_id == null ? '' : (nombres[e.customer_id] ?? 'Cliente'),
                                canal: (
                                    <span className="rv-en-celda">
                                        <span>{e.final_channel == null ? 'Sin canal' : CANALES[e.final_channel]}</span>
                                        {e.destination_hint != null && <small>{e.destination_hint}</small>}
                                    </span>
                                ),
                                llego: (
                                    <span className="rv-en-celda">
                                        {chip === null ? <span>{texto}</span> : <Chip estado={chip}>{texto}</Chip>}
                                        {e.used_fallback === true && (
                                            <small>{`No llegó por ${e.preferred_channel == null ? 'el canal preferido' : CANALES[e.preferred_channel]} · pasó a email`}</small>
                                        )}
                                        {e.resent_from_delivery_id != null && <small>Reenvío</small>}
                                    </span>
                                ),
                                cuando: diaYHora(e.created_at),
                            },
                        };
                    })}
                    vacia={
                        <div className="rv-ca-vacia">
                            <span className="rv-ca-vacia__baldosa"><Icono nombre="send" /></span>
                            <strong>No hay mensajes aquí</strong>
                            <span>{filtro === 0 && cliente === null ? 'Las confirmaciones, pases y recordatorios que salgan aparecerán aquí.' : 'Prueba con otro filtro.'}</span>
                        </div>
                    }
                />
            )}
            {lista !== null && (pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
            )}
            <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="lock" /><span>El teléfono y el email se ven ocultos en parte. Lo que llevaba cada mensaje se guarda 30 días después de la fecha.</span></p>
            {abierta !== null && (
                <FichaDeEntrega
                    key={abierta}
                    {...props}
                    id={abierta}
                    titulo={tituloDe}
                    nombres={nombres}
                    puedeReenviar={permisos.puede(PERMISOS_MENSAJES.reenviar) && !soloLectura}
                    onCerrar={() => { setAbierta(null); }}
                    onReenviada={() => {
                        setAbierta(null);
                        reiniciar();
                        recargar();
                    }}
                />
            )}
        </div>
    );
}

function BuscarCliente({ ops, negocio, movil, onElegir }: PropsPantalla & { onElegir: (c: { id: string; nombre: string }) => void }) {
    const [texto, setTexto] = useState('');
    const [resultados, setResultados] = useState<ResumenCliente[] | null>(null);
    const buscarYa = () => {
        if (texto.trim() === '') {
            return;
        }
        ops.listCustomers(negocio, { q: texto.trim(), limit: 10 })
            .then((l) => { setResultados(l.data); })
            .catch(() => { setResultados([]); });
    };
    return (
        <div className="rv-en-buscar">
            <Campo etiqueta="Buscar un cliente" tipo="search" valor={texto} onChange={setTexto} onEnter={buscarYa} autoFocus />
            <Boton tipo="secundario" icono="search" movil={movil} onClick={buscarYa}>Buscar</Boton>
            {resultados !== null && (
                <div className="rv-ca-lista">
                    {resultados.length === 0 ? (
                        <p className="rv-ca-nota rv-ca-nota--gris" style={{ padding: 16 }}>Ningún cliente con ese nombre, teléfono o email.</p>
                    ) : (
                        resultados.map((c) => (
                            <div key={c.id} className="rv-ca-fila-caja">
                                <button type="button" className="rv-ca-fila rv-ca-fila--abre rv-en-fila-b" onClick={() => { onElegir({ id: c.id, nombre: c.display_name }); }}>
                                    <span className="rv-ca-fila__texto"><strong>{c.display_name}</strong></span>
                                    <Icono nombre="chevron-right" className="rv-ca-chevron" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

function FichaDeEntrega({ ops, negocio, movil, id, titulo, nombres, puedeReenviar, onCerrar, onReenviada }: PropsPantalla & { id: string; titulo: (e: Pick<Entrega, 'message_type'>) => string | null; nombres: Readonly<Record<string, string>>; puedeReenviar: boolean; onCerrar: () => void; onReenviada: () => void }) {
    const [e, setE] = useState<FichaEntrega | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [reenviar, setReenviar] = useState(false);

    useEffect(() => {
        let vivo = true;
        ops.getDelivery(negocio, id)
            .then((x) => {
                if (vivo) {
                    setE(x);
                    setError(null);
                }
            })
            .catch((x: unknown) => {
                if (vivo) {
                    setError(x);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, id, intento]);

    const nombre = e?.customer_id == null ? null : (nombres[e.customer_id] ?? null);
    const [texto, chip] = e === null ? ['', null] : LLEGO[e.status];
    const sePuede = puedeReenviar && e !== null && e.purpose === 'service' && e.resendable === true;

    // G325, G333: un rebote duro del email pasa la entrega a «No llegó» y marca el email como no válido.
    const rebotado = e !== null && e.status === 'failed' && e.attempts.some((a) => a.channel === 'email' && a.reason === 'hard_bounce');
    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            tamano="ficha"
            titulo={e === null ? 'Envío' : (titulo(e) ?? e.deliverable_label ?? 'Mensaje')}
            eyebrow="Envío"
            subtitulo={nombre ?? undefined}
            chip={e === null ? undefined : texto}
            chipEstado={chip ?? 'neutral'}
            icono={e?.final_channel === 'email' ? 'mail' : 'send'}
            accion={sePuede ? 'Reenviar' : ''}
            accionIcono="send"
            onAccion={() => { setReenviar(true); }}
        >
            {error !== null && e === null ? (
                <EstadoError seccion="el envío" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={movil} />
            ) : e === null ? (
                <EstadoCargando seccion="el envío" />
            ) : (
                <div className="rv-ca-cuerpo">
                    <div className="rv-ca-bloque">
                        {e.deliverable_label != null && <div className="rv-ca-dato"><span>Para</span><strong>{e.deliverable_label}</strong></div>}
                        {nombre !== null && <div className="rv-ca-dato"><span>Cliente</span><strong>{nombre}</strong></div>}
                        <div className="rv-ca-dato"><span>Canal</span><strong>{[e.final_channel == null ? 'Sin canal' : CANALES[e.final_channel], e.destination_hint].filter((x) => x != null && x !== '').join(' · ')}</strong></div>
                        {e.locale != null && <div className="rv-ca-dato"><span>Idioma</span><strong>{nombreIdioma(e.locale)}</strong></div>}
                        <div className="rv-ca-dato"><span>Tipo</span><strong>{e.purpose === 'marketing' ? 'Comercial' : 'Servicio'}</strong></div>
                        {e.requested_by !== undefined && <div className="rv-ca-dato"><span>Lo pidió</span><strong>{e.requested_by === 'module' ? e.origin_module ?? 'Reservaria' : (PIDIO[e.requested_by] ?? '')}</strong></div>}
                        {e.items.length > 0 && <div className="rv-ca-dato"><span>Contenía</span><strong>{e.items.map((i) => i.kind).join(', ')}</strong></div>}
                    </div>
                    {e.status === 'failed' && (
                        <div className="rv-ca-caja rv-ca-caja--error">
                            <strong>{rebotado ? 'El email rebotó' : 'No ha llegado por ningún canal'}</strong>
                            <span>{rebotado ? 'Su email no existe o no admite mensajes: en su ficha sale «Email no válido» y no se vuelve a usar. Corrige su contacto en Clientes y reenvíalo.' : 'Avisamos al equipo en la campana. Corrige su contacto en Clientes y reenvíalo.'}</span>
                        </div>
                    )}
                    {e.status === 'discarded' && e.discard_reason != null && (
                        <div className="rv-ca-caja"><strong>No se envió</strong><span>{e.discard_reason === 'no_contact' ? 'No tiene teléfono ni email.' : e.discard_reason === 'season_pause' ? 'En pausa de temporada.' : e.discard_reason === 'profile_anonymized' ? 'La ficha está anonimizada.' : 'Sin permiso comercial o sin envíos disponibles.'}</span></div>
                    )}
                    <div className="rv-ca-bloque">
                        <div className="rv-ca-bloque__cab"><strong>Intentos</strong></div>
                        {e.attempts.length === 0 && <p className="rv-ca-nota rv-ca-nota--gris">Todavía no ha salido.</p>}
                        {e.attempts.map((a) => {
                            const [r, c] = RESULTADO[a.result];
                            return (
                                <div key={a.id} className="rv-en-intento">
                                    <span className="rv-ca-baldosa"><Icono nombre={a.channel === 'email' ? 'mail' : a.channel === 'instagram' ? 'instagram' : 'message-circle'} /></span>
                                    <span className="rv-en-intento__texto">
                                        <strong>{`${a.step === 'fallback' ? 'Respaldo' : 'Principal'} · ${CANALES[a.channel]}`}</strong>
                                        {a.within_window != null && <span>{a.within_window ? 'Dentro de las 24 horas' : 'Fuera de las 24 horas'}</span>}
                                        <span>{[`Enviado ${diaYHora(a.requested_at)}`, a.delivered_at != null ? `entregado ${diaYHora(a.delivered_at)}` : null, a.failed_at != null ? `falló ${diaYHora(a.failed_at)}` : null].filter((x) => x !== null).join(' · ')}</span>
                                        {a.destination_hint != null && <span>{a.destination_hint}</span>}
                                        {a.reason != null && <span className="rv-en-intento__motivo">{MOTIVO[a.reason]}</span>}
                                    </span>
                                    <Chip estado={c}>{r}</Chip>
                                </div>
                            );
                        })}
                    </div>
                    {!sePuede && puedeReenviar && e.purpose === 'service' && <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="info" /><span>Este mensaje ya no se puede reenviar desde aquí. Pídeselo a su módulo (por ejemplo, desde la reserva).</span></p>}
                </div>
            )}
            {reenviar && e !== null && <VentanaReenviar ops={ops} negocio={negocio} movil={movil} entrega={e} titulo={titulo(e) ?? e.deliverable_label ?? 'este mensaje'} onCerrar={() => { setReenviar(false); }} onHecho={onReenviada} />}
        </PanelLateral>
    );
}

function VentanaReenviar({ ops, negocio, movil, entrega, titulo, onCerrar, onHecho }: Pick<PropsPantalla, 'ops' | 'negocio' | 'movil'> & { entrega: FichaEntrega; titulo: string; onCerrar: () => void; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [canal, setCanal] = useState<CanalReenvio>('preferido');
    const [ocupado, setOcupado] = useState(false);
    // G954: un pase no deja elegir; sale siempre por el canal de la compra (el principal del primer intento).
    const esPase = entrega.message_type === 'pases.pase';
    const delaCompra = entrega.attempts.find((a) => a.step !== 'fallback')?.channel ?? entrega.final_channel;
    const enviar = () => {
        setOcupado(true);
        ops.resendDelivery(negocio, entrega.id, esPase ? null : cuerpoReenvio(canal))
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Reenviado. Aquí verás si le llega.' });
                onHecho();
            })
            .catch((e: unknown) => {
                const breve = avisoBreveDeError(e);
                avisar({ tipo: 'error', texto: breve ?? (e instanceof ErrorApi && e.estado === 429 ? 'Ya se ha reenviado hace poco. Espera un poco antes de volver a hacerlo.' : e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : 'No he podido reenviarlo. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };
    return (
        <Ventana
            abierta
            titulo={`Reenviar ${titulo.charAt(0).toLocaleLowerCase('es')}${titulo.slice(1)}`}
            texto="Le llega otra vez al cliente y queda como una entrega nueva."
            accion="Reenviar"
            accionIcono="send"
            ocupado={ocupado}
            ocupadoTexto="Reenviando…"
            movil={movil}
            onAccion={enviar}
            onCerrar={onCerrar}
        >
            {esPase ? (
                <p className="rv-ca-nota rv-ca-nota--gris">
                    <Icono nombre="send" />
                    <span><strong>{delaCompra == null ? 'Sale por donde se mandó' : `Sale por ${CANALES[delaCompra]}, por donde se mandó`}</strong>{' — Un pase se reenvía siempre por el canal de la compra. Si ya no se puede, por email.'}</span>
                </p>
            ) : (
                <div role="radiogroup" aria-label="Por dónde" className="rv-ca-bloque">
                    <OpcionRadio elegida={canal === 'preferido'} onElegir={() => { setCanal('preferido'); }} titulo="El que prefiere" texto="Su canal preferido; si no puede, email." icono="message-circle" />
                    <OpcionRadio elegida={canal === 'email'} onElegir={() => { setCanal('email'); }} titulo="Email" texto="Si dice que no le ha llegado por su canal." icono="mail" />
                </div>
            )}
        </Ventana>
    );
}
